import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Upload, MapPin, Phone, Shield, Camera, Loader2, AlertCircle, Users, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewIssue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { canParticipate, triggerOnboarding } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Geography data (new system)
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [privacyOptions, setPrivacyOptions] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState({ states: false, districts: false, constituencies: false });
  
  // Leader preview when constituency is selected
  const [assignedLeader, setAssignedLeader] = useState(null);
  const [loadingLeader, setLoadingLeader] = useState(false);
  
  // Form state - stores both IDs and names for submission
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    customCategory: '',
    stateId: '',
    stateName: location.state?.prefilledState || '',
    districtId: '',
    districtName: '',
    constituencyId: '',
    constituencyName: location.state?.prefilledConstituency || '',
    privacy: 'public',
    isAnonymous: false,
    phone: '',
    image: null,
    images: [], // Support multiple images
  });
  const [imagePreviews, setImagePreviews] = useState([]);

  // Check profile completion on mount - redirect if incomplete
  useEffect(() => {
    if (!canParticipate()) {
      triggerOnboarding();
      // Navigate back home, user will be shown onboarding modal
      navigate('/', { replace: true });
    }
  }, [canParticipate, triggerOnboarding, navigate]);

  useEffect(() => {
    fetchStates();
    fetchCategories();
    fetchPrivacyOptions();
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.stateId) {
      fetchDistricts(formData.stateId);
      // Also fetch all constituencies for the state (for when district not selected)
      fetchStateConstituencies(formData.stateId);
    } else {
      setDistricts([]);
      setConstituencies([]);
      setAssignedLeader(null);
    }
  }, [formData.stateId]);

  // Fetch constituencies when district changes
  useEffect(() => {
    if (formData.districtId) {
      fetchDistrictConstituencies(formData.districtId);
    }
  }, [formData.districtId]);

  // Fetch assigned leader when constituency changes
  useEffect(() => {
    if (formData.constituencyName) {
      fetchAssignedLeader(formData.constituencyName);
    } else {
      setAssignedLeader(null);
    }
  }, [formData.constituencyName]);

  const fetchAssignedLeader = async (constituencyName) => {
    setLoadingLeader(true);
    try {
      const response = await axios.get(`${API_URL}/api/leaders?constituency=${encodeURIComponent(constituencyName)}&role=MLA&status=active`);
      if (response.data && response.data.length > 0) {
        setAssignedLeader(response.data[0]);
      } else {
        setAssignedLeader(null);
      }
    } catch (error) {
      console.error('Error fetching assigned leader:', error);
      setAssignedLeader(null);
    } finally {
      setLoadingLeader(false);
    }
  };

  const fetchStates = async () => {
    setLoadingGeo(prev => ({ ...prev, states: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/states`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      // Fallback to old endpoint
      try {
        const fallback = await axios.get(`${API_URL}/api/states`);
        const stateList = Object.keys(fallback.data).map(name => ({
          state_id: name.toLowerCase().replace(/\s+/g, '_'),
          name: name
        }));
        setStates(stateList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoadingGeo(prev => ({ ...prev, states: false }));
    }
  };

  const fetchDistricts = async (stateId) => {
    setLoadingGeo(prev => ({ ...prev, districts: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(stateId)}/districts`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setLoadingGeo(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchStateConstituencies = async (stateId) => {
    setLoadingGeo(prev => ({ ...prev, constituencies: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(stateId)}/constituencies`);
      setConstituencies(response.data.constituencies || []);
    } catch (error) {
      console.error('Error fetching state constituencies:', error);
      // Fallback to old endpoint
      try {
        const stateName = states.find(s => s.state_id === stateId)?.name || stateId;
        const fallback = await axios.get(`${API_URL}/api/states`);
        const stateConstituencies = fallback.data[stateName] || [];
        setConstituencies(stateConstituencies.map(name => ({
          constituency_id: name.toLowerCase().replace(/\s+/g, '_'),
          name: name
        })));
      } catch (e) {
        setConstituencies([]);
      }
    } finally {
      setLoadingGeo(prev => ({ ...prev, constituencies: false }));
    }
  };

  const fetchDistrictConstituencies = async (districtId) => {
    setLoadingGeo(prev => ({ ...prev, constituencies: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geo/districts/${encodeURIComponent(districtId)}/constituencies`);
      const districtConstituencies = response.data.constituencies || [];
      // Only update if we got data, otherwise keep state-level constituencies
      if (districtConstituencies.length > 0) {
        setConstituencies(districtConstituencies);
      }
    } catch (error) {
      console.error('Error fetching district constituencies:', error);
    } finally {
      setLoadingGeo(prev => ({ ...prev, constituencies: false }));
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['Road', 'Electricity', 'Water', 'Healthcare', 'Education', 'Public Safety', 'Corruption', 'Government Scheme', 'Other']);
    }
  };

  const fetchPrivacyOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/privacy-options`);
      setPrivacyOptions(response.data.options || []);
    } catch (error) {
      console.error('Error fetching privacy options:', error);
    }
  };

  const handleStateChange = (value) => {
    const selectedState = states.find(s => s.state_id === value);
    setFormData({ 
      ...formData, 
      stateId: value,
      stateName: selectedState?.name || value,
      districtId: '', 
      districtName: '',
      constituencyId: '',
      constituencyName: '' 
    });
  };

  const handleDistrictChange = (value) => {
    const selectedDistrict = districts.find(d => d.district_id === value);
    setFormData({ 
      ...formData, 
      districtId: value,
      districtName: selectedDistrict?.name || value,
      constituencyId: '',
      constituencyName: '' 
    });
  };

  const handleConstituencyChange = (value) => {
    const selectedConstituency = constituencies.find(c => c.constituency_id === value);
    setFormData({ 
      ...formData, 
      constituencyId: value,
      constituencyName: selectedConstituency?.name || value 
    });
  };

  // Image handling - supports multiple images
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 images
    const newFiles = files.slice(0, 5 - formData.images.length);
    
    // Generate previews
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newFiles],
      image: newFiles[0] // Keep backward compatibility
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      image: prev.images.length > 1 ? prev.images[0] : null
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check profile completion before submitting
    if (!canParticipate()) {
      triggerOnboarding();
      return;
    }
    
    // Validation - use new field names
    if (!formData.stateId || !formData.constituencyId) {
      toast({
        title: 'Error',
        description: 'Please select state and constituency',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.category) {
      toast({
        title: 'Error',
        description: 'Please select a category',
        variant: 'destructive',
      });
      return;
    }

    if (formData.category === 'Other' && !formData.customCategory) {
      toast({
        title: 'Error',
        description: 'Please specify the category',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('category', formData.category);
      if (formData.category === 'Other' && formData.customCategory) {
        data.append('custom_category', formData.customCategory);
      }
      // Send the readable names (backend expects names, not IDs)
      data.append('state', formData.stateName);
      if (formData.districtName) {
        data.append('district', formData.districtName);
      }
      data.append('constituency', formData.constituencyName);
      data.append('privacy', formData.privacy);
      data.append('is_anonymous', formData.isAnonymous);
      if (formData.phone) {
        data.append('phone', formData.phone);
      }
      if (formData.image) {
        data.append('image', formData.image);
      }

      const response = await axios.post(`${API_URL}/api/issues`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      toast({
        title: 'Success',
        description: 'Issue reported successfully',
      });
      navigate(`/issues/${response.data.issue_id}`);
    } catch (error) {
      // Handle 403 from backend - check for profile completion message
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.toLowerCase().includes('complete your responsible citizen profile')) {
          triggerOnboarding();
          return;
        }
        // Other 403 errors (e.g., banned user)
        toast({
          title: 'Not Authorized',
          description: detail || 'You are not authorized to perform this action',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to report issue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Report an Issue</h1>
        <p className="text-base text-gray-600 mb-8">Help improve your community by reporting issues to your local leaders</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Issue Details Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              Issue Details
            </h2>
            
            <div>
              <Label htmlFor="title">Issue Title *</Label>
              <Input
                id="title"
                data-testid="issue-title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of the issue"
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="category-select" className="mt-2">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.category === 'Other' && (
              <div>
                <Label htmlFor="customCategory">Specify Category *</Label>
                <Input
                  id="customCategory"
                  data-testid="custom-category-input"
                  value={formData.customCategory}
                  onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                  placeholder="Enter category name"
                  className="mt-2"
                />
              </div>
            )}

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                data-testid="issue-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Provide detailed information about the issue"
                required
                rows={5}
                className="mt-2"
              />
            </div>
          </div>

          {/* Location Card - Updated for new Geography System */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Location
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* State Dropdown */}
              <div>
                <Label htmlFor="state">State *</Label>
                <Select value={formData.stateId} onValueChange={handleStateChange}>
                  <SelectTrigger data-testid="state-select" className="mt-2">
                    {loadingGeo.states ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder="Select state" />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {states.map((state) => (
                      <SelectItem key={state.state_id} value={state.state_id}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* District Dropdown */}
              <div>
                <Label htmlFor="district">District</Label>
                <Select 
                  value={formData.districtId} 
                  onValueChange={handleDistrictChange}
                  disabled={!formData.stateId || loadingGeo.districts}
                >
                  <SelectTrigger data-testid="district-select" className="mt-2">
                    {loadingGeo.districts ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder={districts.length ? "Select district (optional)" : "Select state first"} />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {districts.map((district) => (
                      <SelectItem key={district.district_id} value={district.district_id}>
                        {district.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Constituency Dropdown */}
              <div>
                <Label htmlFor="constituency">Constituency *</Label>
                <Select
                  value={formData.constituencyId}
                  onValueChange={handleConstituencyChange}
                  disabled={!formData.stateId || loadingGeo.constituencies}
                >
                  <SelectTrigger data-testid="constituency-select" className="mt-2">
                    {loadingGeo.constituencies ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder={constituencies.length ? "Select constituency" : "Select state first"} />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {constituencies.map((constituency) => (
                      <SelectItem key={constituency.constituency_id} value={constituency.constituency_id}>
                        {constituency.name}
                        {constituency.reserved_category && (
                          <span className="ml-1 text-xs text-gray-500">({constituency.reserved_category})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Leader Preview - Shows which leader will be assigned */}
            {formData.constituencyName && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  Issue will be assigned to:
                </h3>
                {loadingLeader ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Finding leader...</span>
                  </div>
                ) : assignedLeader ? (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <img
                      src={assignedLeader.photo_url || '/logo.png'}
                      alt={assignedLeader.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-blue-200"
                      onError={(e) => { e.target.src = '/logo.png'; }}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{assignedLeader.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {assignedLeader.role || 'MLA'}
                        </span>
                        <span className="text-xs text-gray-500">{assignedLeader.party}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <span className="text-lg font-bold text-blue-600">{(assignedLeader.avg_rating || 0).toFixed(1)}</span>
                        <span className="text-yellow-400">★</span>
                      </div>
                      <p className="text-xs text-gray-500">{assignedLeader.rating_count || 0} ratings</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-700">
                      No active MLA found for this constituency. Your issue will be reviewed and assigned by an admin.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Image Upload Card - Fixed for mobile gallery + camera */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-600" />
              Photo Evidence
              <span className="text-xs font-normal text-gray-400">(Optional)</span>
            </h2>
            
            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={preview} 
                      alt={`Preview ${index + 1}`} 
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Options */}
            {imagePreviews.length < 5 && (
              <div className="grid grid-cols-2 gap-3">
                {/* Take Photo Button */}
                <label
                  htmlFor="camera-capture"
                  className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-400 transition-all bg-blue-50/30"
                >
                  <div className="flex flex-col items-center">
                    <Camera className="h-8 w-8 text-blue-500 mb-2" />
                    <p className="text-sm text-blue-600 font-medium">Take Photo</p>
                    <p className="text-xs text-blue-400 mt-0.5">Use camera</p>
                  </div>
                  <input
                    id="camera-capture"
                    data-testid="issue-camera-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                
                {/* Upload from Gallery Button */}
                <label
                  htmlFor="image"
                  className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-all"
                >
                  <div className="flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 font-medium">Upload Photo</p>
                    <p className="text-xs text-gray-400 mt-0.5">From gallery</p>
                  </div>
                  <input
                    id="image"
                    data-testid="issue-image-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {imagePreviews.length > 0 && (
              <p className="text-xs text-gray-500">{imagePreviews.length}/5 images uploaded</p>
            )}
          </div>

          {/* Contact Info Card (Optional - No OTP) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Phone className="h-5 w-5 text-blue-600" />
              Contact Information
              <span className="text-xs font-normal text-gray-400">(Optional)</span>
            </h2>
            
            <p className="text-sm text-gray-500">
              Add your phone number if you'd like leaders to contact you directly about this issue.
            </p>

            <Input
              type="tel"
              data-testid="phone-input"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone number (optional)"
            />
          </div>

          {/* Privacy & Anonymous Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Privacy Settings
            </h2>

            <div>
              <Label htmlFor="privacy">Who can see this issue?</Label>
              <Select 
                value={formData.privacy} 
                onValueChange={(v) => setFormData({ ...formData, privacy: v })}
              >
                <SelectTrigger data-testid="privacy-select" className="mt-2">
                  <SelectValue placeholder="Select privacy" />
                </SelectTrigger>
                <SelectContent>
                  {privacyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <span className="text-xs text-gray-500">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label htmlFor="anonymous" className="text-base">Post Anonymously</Label>
                <p className="text-sm text-gray-500">Your name will be shown as "Anonymous Citizen"</p>
              </div>
              <Switch
                id="anonymous"
                data-testid="anonymous-switch"
                checked={formData.isAnonymous}
                onCheckedChange={(checked) => setFormData({ ...formData, isAnonymous: checked })}
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <Button
              type="submit"
              data-testid="submit-issue-btn"
              disabled={loading}
              className="bg-blue-600 text-white hover:bg-blue-700 font-medium px-8 py-3 rounded-full transition-all flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Report Issue'
              )}
            </Button>
            <Button
              type="button"
              data-testid="cancel-issue-btn"
              onClick={() => navigate('/issues')}
              variant="outline"
              className="bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 font-medium px-6 py-3 rounded-full"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewIssue;
